import { Field, FieldLabel, ModuleWrapper } from '@/components'
import { VehicleExpirationTypeCombobox } from '@/components/combobox'
import type {
  VehicleExpirationExportFilters,
  VehicleExpirationsQuery,
  VehicleExpirationsQueryVariables,
} from '@/modules/vehicles'
import { VEHICLE_EXPIRATIONS_QUERY } from '@/modules/vehicles'
import type { VehicleListFilterForm } from '@/modules/vehicles/list-filters'
import {
  emptyVehicleListFilter,
  getVehicleExpirationFilterVariables,
  VehicleListFilters,
} from '@/modules/vehicles/list-filters'
import { VehicleListExportButton } from '@/modules/vehicles/list-export'
import { useDebounce, useNodes, useOnErrorAuto } from '@/utils/hooks'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo, useState } from 'react'
import { useVehicleExpirationHandlers } from './use-vehicle-expiration-handlers'
import { VehicleExpirationsTable } from './vehicle-expirations-table'

/** The shared filters plus the expiration type, which only this page has. */
interface ExpirationFilterForm extends VehicleListFilterForm {
  expirationTypes: string[]
}

const emptyFilter: ExpirationFilterForm = {
  ...emptyVehicleListFilter,
  expirationTypes: [],
}

export const VehicleExpirationsView: React.FC = () => {
  const translations = useTranslate({
    title: 'vehicles.expiration-dates',
    searchPlaceholder: 'vehicles.expirations.search-placeholder',
    type: 'common.type',
  })

  const [filter, setFilter] = useState<ExpirationFilterForm>(emptyFilter)

  // Only the free-text search is debounced; the other filters change one click
  // at a time and should apply immediately.
  const debouncedQ = useDebounce(filter.q)

  // Ordering is applied at the query, not here: the export takes the same
  // filters and its input has no `orderBy`.
  const filterVariables = useMemo<VehicleExpirationExportFilters>(() => {
    const { expirationTypes } = filter

    return {
      ...getVehicleExpirationFilterVariables({ ...filter, q: debouncedQ }),
      expirationTypes: expirationTypes.length > 0 ? expirationTypes : undefined,
    }
  }, [filter, debouncedQ])

  const onErrorAuto = useOnErrorAuto()

  const { data, loading } = useQuery<
    VehicleExpirationsQuery,
    VehicleExpirationsQueryVariables
  >(VEHICLE_EXPIRATIONS_QUERY, {
    variables: { ...filterVariables, orderBy: 'expirationDate' },
    onError: onErrorAuto(),
  })

  const expirations = useNodes(data?.allVehicleExpirations)

  const { editExpiration, removeExpiration, moveToServices } =
    useVehicleExpirationHandlers()

  return (
    <ModuleWrapper
      title={translations.title}
      headerRight={
        <VehicleListExportButton kind="expirations" filters={filterVariables} />
      }
    >
      <VehicleListFilters
        filter={filter}
        searchPlaceholder={translations.searchPlaceholder}
        extraFilterActive={filter.expirationTypes.length > 0}
        onChange={patch => setFilter(current => ({ ...current, ...patch }))}
        onClear={() => setFilter(emptyFilter)}
      >
        <Field>
          <FieldLabel>{translations.type}</FieldLabel>
          <VehicleExpirationTypeCombobox
            mode="multiple"
            value={filter.expirationTypes}
            className="max-w-[350px]"
            onChange={expirationTypes =>
              setFilter(current => ({ ...current, expirationTypes }))
            }
          />
        </Field>
      </VehicleListFilters>

      <VehicleExpirationsTable
        expirations={expirations}
        loading={loading}
        onEdit={editExpiration}
        onDelete={removeExpiration}
        onMoveToServices={moveToServices}
      />
    </ModuleWrapper>
  )
}
